import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Check, RotateCcw, FileCheck, AlertTriangle, Download, Wine, Leaf, Cigarette } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../services/api';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

const CARD_TYPES = [
  { id: 'alcohol', name: 'Alcool', icon: Wine, color: '#722F37', description: 'Carte fidélité Alcool (10 tampons)' },
  { id: 'cbd', name: 'CBD', icon: Leaf, color: '#22C55E', description: 'Carte fidélité CBD (10 tampons)' },
  { id: 'puffs', name: 'Puffs', icon: Cigarette, color: '#3B82F6', description: 'Carte fidélité Puffs (Nicotine)' }
];

export default function ParentalConsentModal({ customer, onUpdate, onClose }) {
  const [signerName, setSignerName] = useState(
    customer?.guardianFirstName && customer?.guardianLastName 
      ? `${customer.guardianFirstName} ${customer.guardianLastName}`
      : ''
  );
  const [authorizedCards, setAuthorizedCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0015';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#00FFFF';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const toggleCard = (cardId) => {
    setAuthorizedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(c => c !== cardId)
        : [...prev, cardId]
    );
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const pos = getPos(e);
    lastPosRef.current = pos;
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);

    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPosRef.current = pos;
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0a0015';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const generatePDF = (signatureData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // En-tête professionnel avec dégradé simulé
    doc.setFillColor(20, 10, 40);
    doc.rect(0, 0, pageWidth, 50, 'F');
    
    // Bande décorative
    doc.setFillColor(139, 92, 246);
    doc.rect(0, 46, pageWidth, 4, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('LA PETITE ÉPICERIE', pageWidth / 2, 18, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(200, 200, 200);
    doc.text('138 Rue Arthur Lamendin - 62980 Vermelles', pageWidth / 2, 26, { align: 'center' });
    
    doc.setFontSize(16);
    doc.setTextColor(0, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('AUTORISATION PARENTALE', pageWidth / 2, 40, { align: 'center' });
    
    // Corps du document
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    let y = 60;
    
    // Informations du mineur
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS DU MINEUR', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.text(`Nom : ${customer?.name || 'Non renseigné'}`, 20, y);
    y += 7;
    doc.text(`Téléphone : ${customer?.phone || 'Non renseigné'}`, 20, y);
    y += 7;
    if (customer?.birthDate) {
      doc.text(`Date de naissance : ${new Date(customer.birthDate).toLocaleDateString('fr-FR')}`, 20, y);
      y += 7;
    }
    
    y += 10;
    
    // Cartes autorisées
    doc.setFont('helvetica', 'bold');
    doc.text('CARTES DE FIDÉLITÉ AUTORISÉES', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    
    CARD_TYPES.forEach(card => {
      const isAuthorized = authorizedCards.includes(card.id);
      const checkbox = isAuthorized ? '☑' : '☐';
      doc.text(`${checkbox} ${card.name} - ${card.description}`, 25, y);
      y += 8;
    });
    
    y += 10;
    
    // Texte légal
    doc.setFont('helvetica', 'bold');
    doc.text('DÉCLARATION', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const declaration = `Je soussigné(e), ${signerName}, responsable légal de ${customer?.name || 'le mineur'}, ` +
      `autorise La Petite Épicerie à lui vendre les produits correspondant aux cartes de fidélité cochées ci-dessus. ` +
      `Je certifie avoir pris connaissance des avertissements légaux concernant ces produits et j'engage ma responsabilité parentale. ` +
      `Cette autorisation est valable pour la durée de participation au programme de fidélité.`;
    
    const lines = doc.splitTextToSize(declaration, pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 15;
    
    // Avertissements
    doc.setFillColor(255, 243, 205);
    doc.rect(15, y - 5, pageWidth - 30, 35, 'F');
    doc.setTextColor(150, 100, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('AVERTISSEMENTS :', 20, y + 3);
    doc.setFont('helvetica', 'normal');
    doc.text("• L'abus d'alcool est dangereux pour la santé, à consommer avec modération.", 20, y + 11);
    doc.text('• La nicotine contenue dans les puffs est fortement addictive.', 20, y + 18);
    doc.text('• Le CBD ne doit pas être consommé avant de conduire.', 20, y + 25);
    
    y += 45;
    
    // Signature
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE DU RESPONSABLE LÉGAL', 20, y);
    y += 10;
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Nom : ${signerName}`, 20, y);
    y += 7;
    doc.text(`Date : ${new Date().toLocaleDateString('fr-FR')}`, 20, y);
    y += 10;
    
    // Ajouter l'image de la signature
    if (signatureData) {
      try {
        doc.addImage(signatureData, 'PNG', 20, y, 80, 40);
      } catch (e) {
        console.error('Erreur ajout signature:', e);
      }
    }
    
    // Pied de page avec infos entreprise
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('─'.repeat(50), pageWidth / 2, 270, { align: 'center' });
    
    doc.setFont('helvetica', 'bold');
    doc.text('LA PETITE ÉPICERIE', pageWidth / 2, 277, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('138 Rue Arthur Lamendin - 62980 Vermelles', pageWidth / 2, 282, { align: 'center' });
    doc.text('SIRET : 944 108 638 00015', pageWidth / 2, 286, { align: 'center' });
    
    doc.setTextColor(128, 128, 128);
    doc.setFontSize(7);
    doc.text(`Document généré le ${new Date().toLocaleString('fr-FR')} - Programme de fidélité Client-Malin`, pageWidth / 2, 292, { align: 'center' });
    
    return doc;
  };

  const handleSubmit = async () => {
    if (!signerName.trim()) {
      toast.error('Veuillez entrer le nom du signataire');
      return;
    }
    if (!hasDrawn) {
      toast.error('Veuillez signer dans la zone prévue');
      return;
    }
    if (authorizedCards.length === 0) {
      toast.error('Veuillez sélectionner au moins une carte à autoriser');
      return;
    }

    setIsLoading(true);
    try {
      const canvas = canvasRef.current;
      const signature = canvas.toDataURL('image/png');
      
      const updatedCustomer = await api.signParentalConsent(
        customer.id,
        signature,
        signerName.trim(),
        authorizedCards
      );
      
      toast.success('Autorisation parentale signée avec succès !');
      onUpdate(updatedCustomer);
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la signature');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const canvas = canvasRef.current;
    const signature = hasDrawn ? canvas.toDataURL('image/png') : null;
    const doc = generatePDF(signature);
    doc.save(`autorisation_parentale_${customer?.name?.replace(/\s/g, '_') || 'client'}.pdf`);
    toast.success('PDF téléchargé !');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto"
    >
      <div className="min-h-screen py-8 px-4 flex items-start justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full backdrop-blur-xl rounded-3xl p-6 border-2 my-4"
          style={{
            background: 'rgba(10, 0, 30, 0.95)',
            borderColor: '#FFD700',
            boxShadow: '0 0 40px rgba(255, 215, 0, 0.4)'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileCheck className="w-6 h-6 text-yellow-400" />
              Autorisation Parentale
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Info client */}
          <div className="mb-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/50">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-yellow-400 font-semibold">Client mineur : {customer?.name}</p>
                <p className="text-gray-400 text-xs mt-1">
                  Sélectionnez les cartes que vous autorisez et signez ci-dessous.
                </p>
              </div>
            </div>
          </div>

          {/* Sélection des cartes */}
          <div className="mb-4">
            <Label className="text-white mb-3 block font-semibold">
              Cartes de fidélité autorisées *
            </Label>
            <div className="space-y-2">
              {CARD_TYPES.map(card => {
                const Icon = card.icon;
                const isChecked = authorizedCards.includes(card.id);
                return (
                  <div
                    key={card.id}
                    onClick={() => toggleCard(card.id)}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      isChecked 
                        ? 'border-green-500 bg-green-500/20' 
                        : 'border-gray-600 bg-black/30 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isChecked ? 'bg-green-500 border-green-500' : 'border-gray-500'
                        }`}
                      >
                        {isChecked && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <Icon className="w-5 h-5" style={{ color: card.color }} />
                      <div className="flex-1">
                        <span className="text-white font-medium">{card.name}</span>
                        <p className="text-xs text-gray-400">{card.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Signer Name */}
          <div className="mb-4">
            <Label className="text-white mb-2 block">Nom complet du responsable légal *</Label>
            <Input
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Prénom et Nom du parent/tuteur"
              className="bg-black/50 border-yellow-500 text-white"
            />
          </div>

          {/* Signature Canvas */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white">Signature *</Label>
              <Button
                onClick={clearCanvas}
                size="sm"
                variant="outline"
                className="text-gray-400 border-gray-600"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Effacer
              </Button>
            </div>
            <div 
              className="rounded-xl overflow-hidden border-2"
              style={{ borderColor: hasDrawn ? '#00FFFF' : '#666' }}
            >
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1 text-center">
              Signez avec votre doigt ou la souris
            </p>
          </div>

          {/* Legal text */}
          <div className="mb-4 p-3 rounded-lg bg-black/30 text-xs text-gray-500">
            <p>
              En signant, je certifie être le responsable légal de {customer?.name} 
              et j&apos;autorise La Petite Épicerie à lui vendre les produits correspondant 
              aux cartes sélectionnées.
            </p>
            <p className="mt-1">
              Date : {new Date().toLocaleDateString('fr-FR')}
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <Button
              onClick={handleSubmit}
              disabled={isLoading || !hasDrawn || !signerName.trim() || authorizedCards.length === 0}
              className="w-full font-bold py-5"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#000'
              }}
            >
              <Check className="w-5 h-5 mr-2" />
              {isLoading ? 'Signature...' : 'Signer l\'autorisation'}
            </Button>
            
            {hasDrawn && signerName.trim() && authorizedCards.length > 0 && (
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                className="w-full border-cyan-500 text-cyan-400"
              >
                <Download className="w-5 h-5 mr-2" />
                Télécharger le PDF
              </Button>
            )}
            
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full border-gray-500 text-gray-300"
            >
              Annuler
            </Button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Composant pour télécharger l'autorisation existante
export function DownloadConsentButton({ customer }) {
  const generateExistingPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // En-tête
    doc.setFillColor(10, 0, 21);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(0, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('LA PETITE ÉPICERIE', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('AUTORISATION PARENTALE', pageWidth / 2, 32, { align: 'center' });
    
    // Corps
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    let y = 55;
    
    // Informations du mineur
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS DU MINEUR', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.text(`Nom : ${customer?.name || 'Non renseigné'}`, 20, y);
    y += 7;
    doc.text(`Téléphone : ${customer?.phone || 'Non renseigné'}`, 20, y);
    y += 7;
    if (customer?.birthDate) {
      doc.text(`Date de naissance : ${new Date(customer.birthDate).toLocaleDateString('fr-FR')}`, 20, y);
      y += 7;
    }
    
    y += 10;
    
    // Cartes autorisées
    doc.setFont('helvetica', 'bold');
    doc.text('CARTES DE FIDÉLITÉ AUTORISÉES', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    
    const authorizedCards = customer?.parentalConsentCards || [];
    CARD_TYPES.forEach(card => {
      const isAuthorized = authorizedCards.includes(card.id);
      const checkbox = isAuthorized ? '☑' : '☐';
      doc.text(`${checkbox} ${card.name} - ${card.description}`, 25, y);
      y += 8;
    });
    
    y += 10;
    
    // Déclaration
    doc.setFont('helvetica', 'bold');
    doc.text('DÉCLARATION', 20, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const declaration = `Je soussigné(e), ${customer?.parentalConsentSignerName || 'le responsable légal'}, ` +
      `responsable légal de ${customer?.name || 'le mineur'}, ` +
      `autorise La Petite Épicerie à lui vendre les produits correspondant aux cartes de fidélité cochées ci-dessus.`;
    
    const lines = doc.splitTextToSize(declaration, pageWidth - 40);
    doc.text(lines, 20, y);
    y += lines.length * 5 + 15;
    
    // Avertissements
    doc.setFillColor(255, 243, 205);
    doc.rect(15, y - 5, pageWidth - 30, 35, 'F');
    doc.setTextColor(150, 100, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('AVERTISSEMENTS :', 20, y + 3);
    doc.setFont('helvetica', 'normal');
    doc.text("• L'abus d'alcool est dangereux pour la santé, à consommer avec modération.", 20, y + 11);
    doc.text('• La nicotine contenue dans les puffs est fortement addictive.', 20, y + 18);
    doc.text('• Le CBD ne doit pas être consommé avant de conduire.', 20, y + 25);
    
    y += 45;
    
    // Signature
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('SIGNATURE DU RESPONSABLE LÉGAL', 20, y);
    y += 10;
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Nom : ${customer?.parentalConsentSignerName || 'Non renseigné'}`, 20, y);
    y += 7;
    if (customer?.parentalConsentDate) {
      doc.text(`Date de signature : ${new Date(customer.parentalConsentDate).toLocaleDateString('fr-FR')}`, 20, y);
      y += 10;
    }
    
    // Ajouter l'image de la signature
    if (customer?.parentalConsentSignature) {
      try {
        doc.addImage(customer.parentalConsentSignature, 'PNG', 20, y, 80, 40);
      } catch (e) {
        console.error('Erreur ajout signature:', e);
      }
    }
    
    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text('La Petite Épicerie - Programme de fidélité', pageWidth / 2, 285, { align: 'center' });
    doc.text(`Document généré le ${new Date().toLocaleString('fr-FR')}`, pageWidth / 2, 290, { align: 'center' });
    
    doc.save(`autorisation_parentale_${customer?.name?.replace(/\s/g, '_') || 'client'}.pdf`);
    toast.success('PDF téléchargé !');
  };

  if (!customer?.parentalConsentSigned) return null;

  return (
    <Button
      onClick={generateExistingPDF}
      size="sm"
      variant="outline"
      className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
    >
      <Download className="w-4 h-4 mr-1" />
      Télécharger l&apos;autorisation
    </Button>
  );
}
